import { ActionTree, ActionContext } from 'vuex';

import { storageClient } from '@/http-client';
import { snakeToCamel, camelToSnake } from '@/utils';
import { getDB } from '@/utils/indexeddb';

import { RootStateI } from '../state';
import { FileI, FilesStateI } from './state';
import { FolderI } from '../folders/state';

function isHlsPlaylistFile(file: Pick<FileI, 'name' | 'contentType'>) {
  const name = file.name?.toLowerCase() || '';
  const contentType = file.contentType?.toLowerCase() || '';

  return name.endsWith('.m3u8') || [
    'application/vnd.apple.mpegurl',
    'application/x-mpegurl',
    'application/mpegurl',
    'audio/x-mpegurl',
  ].includes(contentType);
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export const actions: ActionTree<FilesStateI, RootStateI> = {
  async filter(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: {
      query: string;
      page: number;
      orderBy: string;
      order: string;
      folderId: number | string | null;
    },
  ): Promise<void> {
    // convert the payload to url query params
    let params = '';

    const payloadData = camelToSnake(payload);

    Object.entries(payloadData).forEach(([key, value]) => {
      params += `${key}=${value}&`;
    });

    const { data } = await storageClient.get(`/api/storage/listfiles?${params.toString()}`);
    context.commit('setResult', snakeToCamel(data));
  },

  async upload(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: {
      formData: FormData;
      folderId: number | string | null;
    },
  ): Promise<void> {
    const completedFiles: FileI[] = [];

    try {
      // extract files ONCE
      const files = payload.formData.getAll('file') as File[];

      // populate UI state
      const startIndex = context.state.uploadFiles.length;
      files.forEach((file) => {
        context.state.uploadFiles.push({
          id: 0,
          name: file.name,
          size: file.size,
          contentType: file.type,
          userId: 0,
          r2Key: '',
          r2Url: '',
          uploadCompleted: false,
          error: '',
          created: 0,
          updated: 0,
          folderId: payload.folderId,
          percentage: 0,
        });
      });

      // request upload URLs
      const { data } = await storageClient.post(
        '/api/storage/generate-upload-url',
        camelToSnake(context.state.uploadFiles.slice(startIndex)),
      );

      const dataArray: FileI[] = snakeToCamel(data);

      // Sincronizar IDs reales con la lista de subida del UI
      dataArray.forEach((item: FileI, index: number) => {
        const uiIndex = startIndex + index;
        if (context.state.uploadFiles[uiIndex]) {
          // eslint-disable-next-line no-param-reassign
          context.state.uploadFiles[uiIndex].id = item.id;
        }
      });

      // upload all files in parallel
      await Promise.all(
        dataArray.map(async (item: FileI, index: number) => {
          const file = files[index];
          const uiIndex = startIndex + index;
          if (!file || !item.r2Url) return;

          await new Promise<void>((resolve) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.onprogress = (event) => {
              if (event.lengthComputable) {
                // eslint-disable-next-line no-param-reassign
                context.state.uploadFiles[uiIndex].percentage = Math.round(
                  (event.loaded / event.total) * 100,
                );
              }
            };

            xhr.onload = async () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                // eslint-disable-next-line no-param-reassign
                context.state.uploadFiles[uiIndex].percentage = 100;
                // eslint-disable-next-line no-param-reassign
                item.uploadCompleted = true;
                completedFiles.push(item);

                if (isHlsPlaylistFile({ name: file.name, contentType: file.type })) {
                  try {
                    await context.dispatch('saveCacheFile', {
                      ...item,
                      sourceBlob: file,
                    });
                  } catch (error) {
                    console.warn('Unable to cache uploaded HLS playlist', error);
                  }
                }
              } else {
                // eslint-disable-next-line no-param-reassign
                item.error = `Upload failed (${xhr.status})`;
                // eslint-disable-next-line no-param-reassign
                context.state.uploadFiles[uiIndex].error = item.error;
              }
              resolve();
            };

            xhr.onerror = () => {
              // eslint-disable-next-line no-param-reassign
              item.error = 'Network error during upload';
              // eslint-disable-next-line no-param-reassign
              context.state.uploadFiles[uiIndex].error = item.error;
              resolve();
            };

            xhr.open('PUT', item.r2Url);
            xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
            xhr.send(file);
          });
        }),
      );

      // confirm uploads ONCE
      if (completedFiles.length > 0) {
        await storageClient.post(
          '/api/storage/confirm-uploads',
          camelToSnake(completedFiles),
        );

        completedFiles.forEach((file) => {
          if (!isHlsPlaylistFile(file)) {
            context.dispatch('saveCacheFile', file);
          }
        });
      }

      // refresh list
      await context.dispatch('filter', {
        query: '',
        page: 1,
        folderId: payload.folderId,
      });
    } catch (error) {
      console.error(error);
    }
  },

  async getFileDetails(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: string,
  ): Promise<void> {
    const { data } = await storageClient.get(`/api/storage/filedetails/${payload}`);
    context.commit('setFile', snakeToCamel(data));
    context.dispatch('saveCacheFile', snakeToCamel(data));
  },

  async downloadFile(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: FileI,
  ): Promise<void> {
    const cached = await context.dispatch('getCacheFile', {
      id: payload.id,
    });

    if (cached) {
      console.log('Loaded from cache');

      const link = document.createElement('a');
      link.href = cached;
      link.download = payload.name;
      link.click();

      return;
    }

    console.log('Downloading and caching');

    await context.dispatch('saveCacheFile', payload);

    const base64 = await context.dispatch('getCacheFile', {
      id: payload.id,
    });

    if (base64) {
      const link = document.createElement('a');
      link.href = base64;
      link.download = payload.name;
      link.click();
      return;
    }

    const win = window.open('', '_blank');
    const { data } = await storageClient.get(
      `/api/storage/get-download-url/${payload.id}`,
    );
    const { url } = data;

    if (win) {
      win.location.href = url;
      console.log('file downloaded with opened window');
    } else {
      // fallback por si el popup fue bloqueado
      const linkEl = document.createElement('a');
      linkEl.href = url;
      linkEl.target = '_blank';
      linkEl.click();
      linkEl.remove();
      console.log('file downloaded');
    }
  },

  async saveCacheFile(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: FileI & { sourceBlob?: Blob; url?: string },
  ): Promise<void> {
    console.log('saveCacheFile', payload);
    if (typeof payload.id === 'string' && payload.id.startsWith('shared-')) return;
    const db = await getDB();
    // Skip if not supported
    if (!db) return;

    // if file is more than 10MB, skip caching
    if (payload.size > 10 * 1024 * 1024) {
      console.log('File too large to cache');
      return;
    }

    if (isHlsPlaylistFile(payload) && !payload.sourceBlob) {
      console.log('Skipping cache for HLS playlist');
      return;
    }

    const base64 = payload.sourceBlob
      ? await blobToDataUrl(payload.sourceBlob)
      : await (async () => {
        const { data } = await storageClient.get(`/api/storage/get-download-url/${payload.id}`);
        const { url } = data;

        let blob: Blob;
        try {
          blob = await fetch(url).then((r) => r.blob());
        } catch (error) {
          console.warn('Skipping cache because the file could not be fetched', error);
          return '';
        }

        return blobToDataUrl(blob);
      })();

    if (!base64) return;

    const tx = db.transaction('files', 'readwrite');
    const store = tx.objectStore('files');

    store.put({
      id: payload.id,
      base64,
      name: payload.name,
      type: payload.contentType,
    });

    // eslint-disable-next-line no-return-await
    await new Promise((resolve) => { tx.oncomplete = resolve; });
  },

  async getCacheFile(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: { id: number | string },
  ): Promise<string> {
    const db = await getDB();

    // Skip if not supported
    if (!db) return '';

    const tx = db.transaction('files', 'readonly');
    const store = tx.objectStore('files');

    return new Promise((resolve, reject) => {
      const request = store.get(payload.id);

      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result.base64);
        } else {
          resolve('');
        }
      };

      request.onerror = reject;
    });
  },

  async previewFile(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: FileI,
  ): Promise<void> {
    // open a new window before wait - Safari requires async
    const win = window.open('', '_blank');
    const { data } = await storageClient.get(
      `/api/storage/get-download-url/${payload.id}`,
    );
    const { url } = data;
    if (win) {
      win.location.href = url;
    } else {
      // fallback por si el popup fue bloqueado
      const linkEl = document.createElement('a');
      linkEl.href = url;
      linkEl.target = '_blank';
      linkEl.click();
      linkEl.remove();
    }
    await context.dispatch('saveCacheFile', payload);
  },

  async getDownloadUrl(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: FileI & { url?: string },
  ): Promise<string> {
    if (payload.url) return payload.url;

    const idStr = String(payload.id);
    if (idStr.startsWith('shared-')) {
      const realId = Number(idStr.replace('shared-', ''));
      const sharesModule = (context.rootState as any).shares;
      const sharedFiles = (sharesModule && sharesModule.sharedFiles) || [];
      const found = sharedFiles.find((f: any) => f.id === realId);
      if (found && found.url) {
        return found.url;
      }
    }

    const { data } = await storageClient.get(`/api/storage/get-download-url/${payload.id}`);
    const { url } = data;

    if (!isHlsPlaylistFile(payload)) {
      context.dispatch('saveCacheFile', payload);
    }

    return url;
  },

  async search(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: {
      q: string,
      page: number,
    },
  ): Promise<void> {
    const { data } = await storageClient.get(`/api/storage/search?q=${payload.q}&page=${payload.page}`);
    context.commit('setSearchResult', snakeToCamel(data));
  },

  async moveFilesToFolder(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: FolderI[],
  ): Promise<void> {
    const { data } = await storageClient.post('/api/files/move-files-to-folder', camelToSnake(payload));
    context.commit('setFiles', snakeToCamel(data));
  },

  async changeFileName(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: {
      id: number | string,
      name: string,
    },
  ): Promise<void> {
    const { data } = await storageClient.put('/api/files/change-file-name', camelToSnake(payload));
    console.log('data', data);
  },

  async restoreFiles(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: FileI[],
  ): Promise<void> {
    const { data } = await storageClient.post('/api/trash/restore-files-in-root-folder', camelToSnake(payload));
    console.log('data', data);
  },

  async removeFilesFromTrash(
    context: ActionContext<FilesStateI, RootStateI>,
    payload: FileI[],
  ): Promise<void> {
    const { data } = await storageClient.post('/api/trash/remove-files-definitely', camelToSnake(payload));
    console.log('data', data);
  },

};
